import React from 'react';
import { usePermissions } from '../hooks/usePermissions';
import { Database, Clock, Bell, User, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { COLORS } from '../config/theme';

function DashboardCard({
  icon: Icon,
  iconColor,
  title,
  description,
  bgColor,
  borderColor,
  buttonVariant = 'primary',
  buttonText,
  onClick,
  buttonClick,
}) {
  const isPrimary = buttonVariant === 'primary';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => e.key === 'Enter' && onClick?.()}
      className={[
        // layout
        "group rounded-2xl p-6 cursor-pointer transition-all duration-200",
        "flex flex-col h-full", // ✅ serve per allineare bottoni in basso
        // shadow + motion
        "shadow-[0_8px_24px_rgba(0,0,0,0.05)] hover:-translate-y-1",
        // focus accessibile sulla card
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
      ].join(' ')}
      style={{
        backgroundColor: bgColor,
        border: `1px solid ${borderColor}`,
        // ring offset su bg chiaro
        outlineColor: COLORS.ring,
      }}
    >
      {/* ✅ Icon container: altezza fissa per uniformare */}
      <div className="mb-6 h-12 flex items-start">
        <Icon className="h-10 w-10" style={{ color: iconColor }} />
      </div>

      <h3 className="text-xl font-semibold mb-3" style={{ color: COLORS.text }}>
        {title}
      </h3>

      {/* ✅ Min-height tipografica: rende tutte le descrizioni “alte uguali”
          - 3 righe circa su text-sm/leading-relaxed
          - se vuoi più/meno: cambia minHeight */}
      <p
        className="text-sm leading-relaxed mb-6"
        style={{
          color: COLORS.text2,
          minHeight: '4.8rem', // ~3 righe (1.6 * 3 * 1rem)
        }}
      >
        {description}
      </p>

      {/* ✅ CTA sempre in basso */}
      <button
        className={[
          "w-full mt-auto py-3 px-4 text-sm font-semibold rounded-xl transition-colors",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
        ].join(' ')}
        style={{
          backgroundColor: isPrimary ? COLORS.primary : 'transparent',
          border: isPrimary ? 'none' : `1px solid ${COLORS.primary}`,
          color: isPrimary ? '#FFFFFF' : COLORS.primary,
          // focus ring coerente su entrambi
          boxShadow: 'none',
        }}
        onClick={(e) => {
          e.stopPropagation();
          buttonClick?.();
        }}
        onMouseEnter={(e) => {
          if (isPrimary) e.currentTarget.style.backgroundColor = COLORS.primaryHover;
          else e.currentTarget.style.backgroundColor = COLORS.softBlue;
        }}
        onMouseLeave={(e) => {
          if (isPrimary) e.currentTarget.style.backgroundColor = COLORS.primary;
          else e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        {buttonText}
      </button>
    </div>
  );
}

const DashboardPage = () => {
  const { hasPermission, isAssociato, isGuest } = usePermissions();
  const navigate = useNavigate();

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
          {/* Banner Guest */}
          {isGuest() && (
            <div className="mb-8 rounded-2xl border border-yellow-200 bg-yellow-50 p-5">
              <p className="text-sm leading-relaxed" style={{ color: COLORS.text }}>
                <strong>L&apos;utenza GUEST</strong> consente solo di accedere <strong>AL MOTORE DI RICERCA DELLE GARE</strong>.
                Per utilizzare tutte le funzionalità, incluso l&apos;accesso ai link dei documenti di gara e al sistema di profilazione per l&apos;invio automatico degli avvisi via email, è necessario associarsi a DS Consulting.
              </p>
            </div>
          )}

          {/* Hero */}
          <div className="text-center mb-14">
            <h1 className="font-display text-4xl font-medium mb-4" style={{ color: COLORS.text }}>
              Motore Gare DS
            </h1>
            <p className="text-lg" style={{ color: COLORS.text2 }}>
              Gestisci le tue ricerche, le notifiche e i risultati delle gare d&apos;appalto.
            </p>
          </div>

          {/* Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto items-stretch">
            {hasPermission('view_storico') && (
              <DashboardCard
                icon={Database}
                iconColor={COLORS.primary}
                title="Banca Dati Gare d'Appalto"
                description="Visualizza e filtra tutte le gare raccolte in piattaforma con ricerca avanzata e filtri personalizzati."
                bgColor={COLORS.softBlue}
                borderColor={COLORS.primary}
                buttonVariant="primary"
                buttonText="Accedi alla Banca Dati"
                onClick={() => navigate('/storico-gare')}
                buttonClick={() => navigate('/storico-gare')}
              />
            )}

            {hasPermission('view_scheduler') && (
              <DashboardCard
                icon={Clock}
                iconColor={COLORS.primary}
                title="Pianifica le Notifiche Automatiche"
                description="Configura notifiche email automatiche per ricevere aggiornamenti sulle gare che ti interessano."
                bgColor="#FFFFFF"
                borderColor={COLORS.border}
                buttonVariant="outline"
                buttonText="Configura Notifiche"
                onClick={() => navigate('/scheduler')}
                buttonClick={() => navigate('/scheduler')}
              />
            )}

            {(isAssociato() || isGuest() || hasPermission('manage_users')) && (
              <DashboardCard
                icon={Bell}
                iconColor={COLORS.accent}
                title="Notifiche"
                description="Visualizza lo storico delle notifiche email inviate e consulta i dettagli delle gare notificate."
                bgColor={COLORS.softYellow}
                borderColor={COLORS.accent}
                buttonVariant="primary"
                buttonText="Visualizza Notifiche"
                onClick={() => navigate('/notifiche')}
                buttonClick={() => navigate('/notifiche')}
              />
            )}

            {hasPermission('manage_users') && (
              <DashboardCard
                icon={User}
                iconColor={COLORS.primary}
                title="Gestione Ruoli"
                description="Gestisci gli utenti e i loro ruoli, modifica permessi e configurazioni di accesso."
                bgColor="#FFFFFF"
                borderColor={COLORS.border}
                buttonVariant="outline"
                buttonText="Gestisci Utenti"
                onClick={() => navigate('/user-management')}
                buttonClick={() => navigate('/user-management')}
              />
            )}

            {hasPermission('manage_users') && (
              <DashboardCard
                icon={Settings}
                iconColor={COLORS.primary}
                title="Impostazioni (Admin)"
                description="Avvia scraping PubblicitàLegale, gestisci schedulazioni e archiviazione automatica."
                bgColor="#FFFFFF"
                borderColor={COLORS.border}
                buttonVariant="outline"
                buttonText="Apri Impostazioni"
                onClick={() => navigate('/settings')}
                buttonClick={() => navigate('/settings')}
              />
            )}
          </div>
    </div>
  );
};

export default DashboardPage;
